import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Signature } from 'lucide-react';
import { createRoot } from 'react-dom/client';
import SignatureCanvas from 'react-signature-canvas';

import { Button } from '@/components/ui/button';
import HelpSection from './helpsection';
import SignatureHeader from './signatureheader';

interface SignatureProps {
  buyersignature?: string;
  sellersignature?: string;
  agentSignature?: string;
  onSignatureSave: (signature: string) => void;
}

const UserSignatureCanvas = ({
  buyersignature,
  sellersignature,
  agentSignature,
  onSignatureSave,
}: SignatureProps) => {
  const [signature, setSignature] = useState<string | null>(
    buyersignature || sellersignature || agentSignature || null
  );
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(Boolean(signature));
  const canvasRef = useRef<SignatureCanvas>(null);

  const handleClear = useCallback(() => {
    canvasRef.current?.clear();
    setSignature(null);
    setHasSignature(false);
    onSignatureSave('');
  }, [onSignatureSave]);

  const handleBegin = useCallback(() => {
    setIsDrawing(true);
  }, []);

  // Save after every stroke, so there's no separate "accept" / "add" step.
  const handleEnd = useCallback(() => {
    setIsDrawing(false);
    const pad = canvasRef.current;
    if (!pad || pad.isEmpty()) {
      setHasSignature(false);
      return;
    }
    let dataURL: string;
    try {
      dataURL = pad.getTrimmedCanvas().toDataURL();
    } catch {
      dataURL = pad.toDataURL();
    }
    setSignature(dataURL);
    setHasSignature(true);
    onSignatureSave(dataURL);
  }, [onSignatureSave]);

  return (
    <div className="max-w-4xl rounded-lg shadow-lg mt-4">
      {/* Header Section */}
      <SignatureHeader />

      {/* Signature Canvas Section */}
      <div className="mb-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Signature Area</h3>
          <p className="text-sm text-gray-500 mb-3">
            Click and drag to sign. Your signature is saved as you draw; use
            Clear Signature to start over.
          </p>
        </div>

        {/* Canvas Container */}
        <div className="relative">
          <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50 hover:border-blue-400 transition-colors duration-200">
            <SignatureCanvas
              penColor="black"
              backgroundColor="transparent"
              // No width/height: the library then sizes the drawing buffer
              // to the CSS box (× devicePixelRatio) and again on resize. A
              // fixed 500px buffer inside the wider box only took strokes on
              // its left half.
              canvasProps={{
                className:
                  'sigCanvas border border-gray-200 rounded-md bg-white shadow-inner',
                style: {
                  cursor: 'crosshair',
                  display: 'block',
                  width: '100%',
                  height: 240,
                  touchAction: 'none',
                },
              }}
              ref={canvasRef}
              onBegin={handleBegin}
              onEnd={handleEnd}
            />

            {/* Drawing Indicator */}
            {isDrawing && (
              <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium">
                Drawing...
              </div>
            )}
          </div>

          {/* Canvas Instructions */}
          <div className="mt-2 text-xs text-gray-500 text-center">
            {!hasSignature ? (
              <span className="text-amber-600 font-medium">
                Please sign in the area above
              </span>
            ) : (
              <span className="text-green-600 font-medium">
                ✓ Signature captured
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Signature Tools
        </h3>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={handleClear}
            className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Clear Signature
          </Button>
        </div>
      </div>

      {/* Signature Preview */}
      {signature && (
        <>
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-3">Signature Preview</h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">Your signature:</span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  ✓ Saved
                </span>
              </div>
              <div className="bg-white rounded border p-4 flex justify-center">
                <img
                  src={signature}
                  alt="Your Signature"
                  className="max-w-full max-h-32 object-contain"
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Help Section */}
      <HelpSection />
    </div>
  );
};

export default UserSignatureCanvas;
